import { businessFeedInfiniteQueryOptions } from '@/api/queries/activity';
import { businessDetailQueryOptions } from '@/api/queries/businesses';
import { menuItemsListQueryOptions } from '@/api/queries/menu-items';
import { RatingDistributionChart } from '@/components/display/rating-distribution-chart';
import { ReviewFilters } from '@/components/forms/review-filters';
import { ScreenHeader } from '@/components/layout/screen-header';
import { PageTitle } from '@/components/seo/page-title';
import { ActivityCard } from '@/features/activity/components/activity-card';
import { useLikeCheckIn } from '@/features/activity/hooks/use-like-check-in';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocalSearchParams } from 'expo-router';
import { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Text, View } from 'react-native';

export function BusinessReviewsScreen() {
  const theme = useAppTheme();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { toggleLike } = useLikeCheckIn();

  // Filter state
  const [selectedRatingKey, setSelectedRatingKey] = useState<string | undefined>();
  const [selectedMenuItemIds, setSelectedMenuItemIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText, 400);

  // Rating selection from filter chips (already toggled by caller)
  const handleRatingSelect = useCallback((key: string | undefined) => {
    setSelectedRatingKey(key);
  }, []);

  const handleClearAll = useCallback(() => {
    setSearchText('');
    setSelectedRatingKey(undefined);
    setSelectedMenuItemIds([]);
  }, []);

  const handleBarPress = useCallback((key: string) => {
    setSelectedRatingKey((prev) => (prev === key ? undefined : key));
  }, []);

  // Data queries
  const { data: business } = useQuery(businessDetailQueryOptions(id || ''));

  const { data: menuItemsData } = useQuery(
    menuItemsListQueryOptions({
      business_id: id || '',
      per_page: 100,
      sort_by: 'name',
    })
  );
  const menuItems = menuItemsData?.items ?? [];

  const filters = useMemo(
    () => ({
      rating_key: selectedRatingKey,
      menu_item_ids: selectedMenuItemIds.length > 0 ? selectedMenuItemIds : undefined,
      search: debouncedSearch || undefined,
    }),
    [selectedRatingKey, selectedMenuItemIds, debouncedSearch]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(businessFeedInfiniteQueryOptions(id || '', filters));

  const allItems = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );

  const renderFooter = useCallback(() => {
    if (!hasNextPage) return null;
    if (isFetchingNextPage) {
      return (
        <View className="py-4 items-center">
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      );
    }
    return null;
  }, [hasNextPage, isFetchingNextPage, theme.colors.primary]);

  const renderEmpty = useCallback(() => {
    if (isLoading) {
      return (
        <View className="py-12 items-center">
          <ActivityIndicator color={theme.colors.primary} />
        </View>
      );
    }
    return (
      <View className="items-center py-12 px-8">
        <Ionicons name="chatbubble-outline" size={48} className="text-muted-foreground" />
        <Text className="text-muted-foreground mt-3 text-base text-center">
          No reviews match your filters
        </Text>
      </View>
    );
  }, [isLoading, theme.colors.primary]);

  const headerTitle = business?.name ? `${business.name} Reviews` : 'Reviews';

  return (
    <View className="flex-1 bg-background">
      <PageTitle title={headerTitle} />
      <ScreenHeader title={headerTitle} showBackButton />

      <FlatList
        data={allItems}
        keyExtractor={(item) => item.checkIn.id}
        renderItem={({ item }) => <ActivityCard item={item} onLike={toggleLike} />}
        ListHeaderComponent={
          <View style={{ marginHorizontal: -16 }}>
            {/* Rating Distribution Chart */}
            {business?.ratingDistribution && (
              <RatingDistributionChart
                distribution={business.ratingDistribution}
                selectedRatingKey={selectedRatingKey}
                onBarPress={handleBarPress}
              />
            )}

            <View className="h-px bg-border mx-4 my-2" />

            {/* Filters */}
            <ReviewFilters
              searchText={searchText}
              onSearchChange={setSearchText}
              menuItems={menuItems}
              selectedMenuItemIds={selectedMenuItemIds}
              onMenuItemSelect={setSelectedMenuItemIds}
              selectedRatingKey={selectedRatingKey}
              onRatingSelect={handleRatingSelect}
              onClearAll={handleClearAll}
            />

            <View className="h-3" />
          </View>
        }
        ListFooterComponent={renderFooter}
        ListEmptyComponent={renderEmpty}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 24 }}
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage) fetchNextPage();
        }}
        onEndReachedThreshold={0.5}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
}
